import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { ArrowLeft, TrendingUp, DollarSign, Package, Calendar, PieChart, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  'nacional-aereo': 'Nacional Aéreo',
  'internacional': 'Internacional',
  'nacional-rodoviario': 'Rodoviário',
  'rodoviario': 'Rodoviário',
  'passeio-1-dia': 'Passeio 1 Dia'
};

const CATEGORY_COLORS = {
  'nacional-aereo': '#3B82F6',      // Azul
  'internacional': '#10B981',        // Verde
  'nacional-rodoviario': '#F59E0B', // Amarelo
  'rodoviario': '#F59E0B',          // Amarelo
  'passeio-1-dia': '#EF4444'        // Vermelho
};

const AdminFinanceiro = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [manualReservations, setManualReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('anual');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadReservations();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(loadReservations, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const [regularRes, manualRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`)
      ]);
      
      // Filtrar reservas pagas (regulares)
      const paidRegular = regularRes.data.filter(r => 
        r.status === 'confirmed' || r.status === 'completed' || r.payment_status === 'paid' || r.payment_status === 'approved'
      );
      
      // Filtrar reservas pagas (manuais) - incluindo 'atrasado' que ainda conta como venda
      const paidManual = manualRes.data.filter(r => 
        r.status === 'pago_total' || r.status === 'pago_parcial' || r.status === 'atrasado'
      );
      
      setReservations(paidRegular);
      setManualReservations(paidManual);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
      setReservations([]);
      setManualReservations([]);
    } finally {
      setLoading(false);
    }
  };

  // Combina todas as reservas, usando amount_paid para reservas manuais com parcelas
  const allReservations = useMemo(() => {
    const processedManual = manualReservations.map(r => {
      // Se tem parcelas, calcular valor efetivamente pago
      if (r.installments && r.installments.length > 0) {
        const paidAmount = r.installments
          .filter(i => i.status === 'pago')
          .reduce((sum, i) => sum + (parseFloat(i.value) || 0), 0);
        return { ...r, paid_amount: paidAmount };
      }
      return { ...r, paid_amount: r.amount_paid || r.total_amount || 0 };
    });
    return [...reservations, ...processedManual];
  }, [reservations, manualReservations]);

  // Filtra por categoria selecionada
  const filteredReservations = useMemo(() => {
    if (selectedCategory === 'all') {
      return allReservations;
    }
    return allReservations.filter(r => {
      const cat = r.package_category || '';
      if (selectedCategory === 'rodoviario') {
        return cat === 'rodoviario' || cat === 'nacional-rodoviario';
      }
      return cat === selectedCategory;
    });
  }, [allReservations, selectedCategory]);

  // Obtém categorias únicas
  const uniqueCategories = useMemo(() => {
    const cats = new Set();
    allReservations.forEach(r => {
      if (r.package_category) {
        cats.add(r.package_category);
      }
    });
    return Array.from(cats);
  }, [allReservations]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const monthsFull = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  // Agrupar dados por mês (anual) ou por dia (mensal)
  const getGroupedData = () => {
    if (viewMode === 'anual') {
      const monthlyData = Array(12).fill(null).map(() => ({ count: 0, value: 0 }));
      
      filteredReservations.forEach(res => {
        const date = new Date(res.created_at);
        if (date.getFullYear() === selectedYear) {
          const month = date.getMonth();
          monthlyData[month].count += 1;
          monthlyData[month].value += res.total_amount || 0;
        }
      });
      
      return monthlyData.map((data, index) => ({
        label: months[index],
        count: data.count,
        value: data.value
      }));
    } else {
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const dailyData = Array(daysInMonth).fill(null).map(() => ({ count: 0, value: 0 }));
      
      filteredReservations.forEach(res => {
        const date = new Date(res.created_at);
        if (date.getFullYear() === selectedYear && date.getMonth() + 1 === selectedMonth) {
          const day = date.getDate() - 1;
          dailyData[day].count += 1;
          dailyData[day].value += res.total_amount || 0;
        }
      });
      
      return dailyData.map((data, index) => ({
        label: (index + 1).toString(),
        count: data.count,
        value: data.value
      }));
    }
  };

  const groupedData = getGroupedData();
  const maxCount = Math.max(...groupedData.map(d => d.count), 1);
  const maxValue = Math.max(...groupedData.map(d => d.value), 1);
  const totalCount = groupedData.reduce((acc, d) => acc + d.count, 0);
  const totalValue = groupedData.reduce((acc, d) => acc + d.value, 0);

  // Dados do gráfico de pizza - AGORA mostra DESTINOS quando categoria é selecionada
  const getPieChartData = () => {
    if (selectedCategory === 'all') {
      // Mostra categorias
      const categoryCounts = {};
      filteredReservations.forEach(r => {
        let cat = r.package_category || 'outros';
        if (cat === 'nacional-rodoviario') cat = 'rodoviario';
        categoryCounts[cat] = (categoryCounts[cat] || 0) + (r.total_amount || 0);
      });
      
      const total = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
      
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
      
      return Object.entries(categoryCounts)
        .map(([key, value], index) => ({
          key,
          label: CATEGORY_LABELS[key] || key,
          value,
          color: CATEGORY_COLORS[key] || colors[index % colors.length],
          percent: total > 0 ? Math.round((value / total) * 100) : 0
        }))
        .filter(c => c.value > 0)
        .sort((a, b) => b.value - a.value);
    } else {
      // Mostra DESTINOS (pacotes) da categoria selecionada - soma se tiver mais de 1 data
      const packageValues = {};
      filteredReservations.forEach(r => {
        const pkgName = r.package_name || 'Sem nome';
        packageValues[pkgName] = (packageValues[pkgName] || 0) + (r.total_amount || 0);
      });
      
      const total = Object.values(packageValues).reduce((a, b) => a + b, 0);
      
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16'];
      
      return Object.entries(packageValues)
        .map(([key, value], index) => ({
          key,
          label: key,
          value,
          color: colors[index % colors.length],
          percent: total > 0 ? Math.round((value / total) * 100) : 0
        }))
        .filter(c => c.value > 0)
        .sort((a, b) => b.value - a.value);
    }
  };

  const pieChartData = getPieChartData();
  const totalPieValue = pieChartData.reduce((acc, c) => acc + c.value, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/admin')} className="flex items-center space-x-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="h-6 w-px bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-6 h-6" />
              <h1 className="text-xl font-bold">Painel Financeiro</h1>
            </div>
          </div>
          <Button onClick={loadReservations} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
            Atualizar
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtro de Categoria */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <Label className="font-medium">Filtrar por Categoria:</Label>
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {uniqueCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat] || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCategory !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedCategory('all')}>
                  Limpar filtro
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total de Vendas</p>
                  <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              {selectedCategory !== 'all' && (
                <p className="text-xs text-gray-500 mt-2">Filtrado: {CATEGORY_LABELS[selectedCategory] || selectedCategory}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Receita Total</p>
                  <p className="text-2xl font-bold text-green-600">{formatPrice(totalValue)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ticket Médio</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatPrice(totalCount > 0 ? totalValue / totalCount : 0)}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Período</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {viewMode === 'anual' ? selectedYear : `${monthsFull[selectedMonth - 1]}/${selectedYear}`}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Calendar className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros de Período */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Visualização:</span>
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="mensal">Mensal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Ano:</span>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2023, 2024, 2025, 2026].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {viewMode === 'mensal' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Mês:</span>
                  <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthsFull.map((month, index) => (
                        <SelectItem key={index + 1} value={(index + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Gráfico de Barras - Vendas */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <Package className="w-5 h-5 mr-2 text-blue-600" />
                Vendas {viewMode === 'anual' ? 'por Mês' : 'por Dia'}
              </h2>
              
              <div className="h-64 flex items-end justify-between gap-1">
                {groupedData.map((item, index) => {
                  const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex flex-col items-center justify-end h-48">
                        {item.count > 0 && (
                          <span className="text-xs font-bold text-blue-600 mb-1">{item.count}</span>
                        )}
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                          style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 mt-2 transform -rotate-45 origin-left">
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Pizza - Categorias ou Destinos */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-purple-600" />
                {selectedCategory === 'all' ? 'Receita por Categoria' : `Destinos - ${CATEGORY_LABELS[selectedCategory] || selectedCategory}`}
              </h2>
              
              {pieChartData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-gray-500">
                  Nenhum dado disponível para o período
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    {/* SVG Pie Chart */}
                    <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                      {(() => {
                        let startAngle = 0;
                        return pieChartData.map((cat, index) => {
                          const angle = totalPieValue > 0 ? (cat.value / totalPieValue) * 360 : 0;
                          const endAngle = startAngle + angle;
                          const largeArcFlag = angle > 180 ? 1 : 0;
                          
                          const startX = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                          const startY = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                          const endX = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                          const endY = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                          
                          const pathD = angle >= 360 
                            ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                            : `M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
                          
                          startAngle = endAngle;
                          
                          return (
                            <path
                              key={index}
                              d={pathD}
                              fill={cat.color}
                              className="hover:opacity-80 transition-opacity cursor-pointer"
                            />
                          );
                        });
                      })()}
                    </svg>
                  </div>
                  
                  {/* Legenda */}
                  <div className="ml-6 space-y-2 max-h-48 overflow-y-auto">
                    {pieChartData.map((cat, index) => (
                      <div key={index} className="flex items-center">
                        <div 
                          className="w-4 h-4 rounded mr-2 flex-shrink-0" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="text-sm text-gray-700">
                          {cat.label.length > 20 ? cat.label.substring(0, 20) + '...' : cat.label}: 
                          <strong className="ml-1">{formatPrice(cat.value)}</strong>
                          <span className="text-gray-500 ml-1">({cat.percent}%)</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Valores */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Receita {viewMode === 'anual' ? 'por Mês' : 'por Dia'}
            </h2>
            
            <div className="h-64 flex items-end justify-between gap-1">
              {groupedData.map((item, index) => {
                const height = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center justify-end h-48">
                      {item.value > 0 && (
                        <span className="text-xs font-bold text-green-600 mb-1">
                          {(item.value / 1000).toFixed(1)}k
                        </span>
                      )}
                      <div 
                        className="w-full bg-green-500 rounded-t transition-all duration-300 hover:bg-green-600"
                        style={{ height: `${height}%`, minHeight: item.value > 0 ? '8px' : '0' }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 mt-2 transform -rotate-45 origin-left">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Nota */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Nota:</strong> Este painel considera reservas do site e reservas manuais com pagamento confirmado. 
            {selectedCategory !== 'all' && (
              <span className="ml-1">Filtro ativo: <strong>{CATEGORY_LABELS[selectedCategory] || selectedCategory}</strong></span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminFinanceiro;
